import axios from "axios";
import QueryString from "qs";
import { useEffect, useMemo, useState } from "react";
import { ReturnedPickupPoints } from "~/pages/api/shipmondo/pickup-points/index.route";
import {
  ListQuotesBody,
  ReturnedQuotes,
} from "~/pages/api/shipmondo/quotes/index.route";
import { useStoreCart } from "~/pages/cart/StoreCart/useStoreCart";
import { Address } from "~/shared-types";
import { components } from "~/shipmondo-schema-v3";

export const CARRIER_PRODUCT_CODES = {
  pdk: { homeDelivery: "PDK_MH", pickupPoint: "PDK_MC" },
  gls: { homeDelivery: "GLSDK_HD", pickupPoint: "GLSDK_SD" },
  dao: { homeDelivery: "DAO_STH", pickupPoint: "DAO_STS" },
  bring: { homeDelivery: "BRI_HDP", pickupPoint: "BRI_PP" },
} as const;

export type PickupPointWithQuote = components["schemas"]["ServicePoint"] & {
  quote?: components["schemas"]["CreateQuoteResponse"];
};

export const usePickupPointSelector = () => {
  const [pickupPoints, setPickupPoints] = useState<
    Array<components["schemas"]["ServicePoint"]>
  >([]);
  const [quotes, setQuotes] = useState<
    components["schemas"]["CreateQuoteResponse"][]
  >([]);

  const {
    cart,
    receiverAddress,
    carrierCode,
    setSavedValues,
    hasFreeDelivery,
  } = useStoreCart();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (
      !receiverAddress?.postCode ||
      !receiverAddress?.line1 ||
      !receiverAddress?.city ||
      !cart?.store.id
    )
      return;

    setIsLoading(true);

    const query = QueryString.stringify({
      address: receiverAddress.line1,
      city: receiverAddress.city,
      zipcode: receiverAddress.postCode,
      address2: receiverAddress.line2,
      storeId: cart?.store.id,
      carrierCode,
      limit: 10,
    });

    const getPickupPoints = axios.get<ReturnedPickupPoints>(
      `/api/shipmondo/pickup-points?${query}`
    );

    const getQuoutesBody: ListQuotesBody = {
      receiver: {
        address1: receiverAddress.line1,
        city: receiverAddress.city,
        zipcode: receiverAddress.postCode,
        address2: receiverAddress.line2,

        // eslint-disable-next-line camelcase
        country_code: "DK",
      },
      cart,
    };

    const listQuotes = axios.post<ReturnedQuotes>(
      `/api/shipmondo/quotes?${query}`,
      getQuoutesBody
    );

    Promise.all([getPickupPoints, listQuotes])
      .then(([pickupPoints, quotes]) => {
        setPickupPoints(pickupPoints.data);
        setQuotes(quotes.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [JSON.stringify(receiverAddress), carrierCode]);

  const pickupPointsWithQuotes: PickupPointWithQuote[] = useMemo(
    () =>
      pickupPoints.map((pickupPoint) => {
        if (!carrierCode) return pickupPoint;

        const quote = quotes.find(
          (quote) =>
            quote.product_code ===
            CARRIER_PRODUCT_CODES[carrierCode].pickupPoint
        );

        return { ...pickupPoint, quote };
      }),
    [JSON.stringify(pickupPoints), JSON.stringify(quotes), carrierCode]
  );

  const homeDeliveryQuote = useMemo(() => {
    if (!carrierCode) return undefined;

    return quotes.find(
      (quote) =>
        quote.product_code === CARRIER_PRODUCT_CODES[carrierCode].homeDelivery
    );
  }, [JSON.stringify(quotes), carrierCode]);

  const onSelectLocation = (
    quote: PickupPointWithQuote["quote"],
    pickupPoint?: PickupPointWithQuote
  ) => {
    let address: Address;

    if (pickupPoint?.address && pickupPoint?.zipcode && pickupPoint?.city) {
      address = {
        line1: pickupPoint.address,
        line2: pickupPoint.address2,
        postCode: pickupPoint.zipcode,
        city: pickupPoint.city,
      };
    }

    setSavedValues((prev) => ({
      ...prev,
      currentDeliveryLocation: {
        quote,
        address,
        servicePointId: pickupPoint?.id,
      },
    }));
  };

  return {
    pickupPointsWithQuotes,
    homeDeliveryQuote,
    isLoading,
    onSelectLocation,
    hasFreeDelivery,
  };
};
